import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Upload, Loader2, History, Undo2, ShieldCheck, FileWarning, Check } from "lucide-react";

const fmtBR = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminShopRestore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const inpRef = useRef(null);
  const [shop, setShop] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("insert_missing");
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);

  const loadShop = useCallback(() => api.get("/shops").then(({ data }) => setShop((data || []).find((s) => s.id === id) || null)).catch(() => {}), [id]);
  const loadHist = useCallback(() => api.get(`/admin/shops/${id}/restore/history`).then(({ data }) => setHistory(data?.items || [])).catch(() => {}), [id]);
  useEffect(() => { loadShop(); loadHist(); }, [loadShop, loadHist]);

  const doPreview = async () => {
    if (!file) { toast.error("Escolha o arquivo .json ou .tar.gz"); return; }
    setBusy("preview"); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/shops/${id}/restore/preview`, fd);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const doRestore = async () => {
    if (!file) { toast.error("Envie o arquivo"); return; }
    if (!shop) { return; }
    if (confirmName.trim() !== (shop.name || "").trim()) { toast.error(`Digite exatamente: ${shop.name}`); return; }
    if (!window.confirm(`Restaurar '${shop.name}' no modo "${mode === "replace" ? "Voltar tudo como estava" : "Só repor o que sumiu"}"?`)) { return; }
    setBusy("restore");
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mode", mode); fd.append("confirm_name", confirmName);
      const { data } = await api.post(`/admin/shops/${id}/restore`, fd);
      toast.success(`Restauração concluída: ${data.inserted} novos, ${data.updated} alterados`);
      setPreview(null); setFile(null); setConfirmName(""); if (inpRef.current) inpRef.current.value = "";
      loadHist();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const doUndo = async () => {
    if (!window.confirm("Desfazer a última restauração desta oficina?")) { return; }
    setBusy("undo");
    try {
      const { data } = await api.post(`/admin/shops/${id}/restore/undo`);
      toast.success(`Desfeito: ${data.updated} registros voltaram ao estado anterior`);
      loadHist();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const hasUndoable = history.some((h) => !h.undone && h.snapshot_id);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="bg-zinc-900 border-zinc-800"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
        <h1 className="font-display text-2xl font-bold uppercase text-zinc-100">Restaurar oficina</h1>
      </div>

      {shop && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-4 text-sm text-zinc-300">
          <div className="font-display text-lg text-zinc-100">{shop.name}</div>
          <div className="text-zinc-500">ID: {shop.id}</div>
          <ul className="mt-2 text-zinc-400 list-disc list-inside space-y-1">
            <li>Aceita o <b>.json</b> do backup do dono ou o <b>.tar.gz</b> do backup completo (nele só são usados os dados desta oficina).</li>
            <li>Antes de restaurar, geramos um snapshot do estado atual para permitir <b>Desfazer</b>.</li>
            <li>Senhas, PINs, tokens, certificados e a conexão do Drive <b>não</b> são sobrescritos.</li>
            <li>Usuários que forem recriados ficam sem senha e precisam redefinir.</li>
          </ul>
        </div>
      )}

      <div data-testid="restore-upload" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-amber-300" />
          <h2 className="font-display uppercase font-bold text-zinc-100">1. Enviar backup</h2>
        </div>
        <input ref={inpRef} type="file" accept=".json,.tar.gz,.tgz,application/json,application/gzip" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
               data-testid="restore-file" className="block w-full text-sm text-zinc-200 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-amber-500 file:text-black file:font-bold" />
        <Button data-testid="restore-preview-btn" disabled={!file || busy} onClick={doPreview} className="bg-sky-600 hover:bg-sky-500 text-white font-bold uppercase">
          {busy === "preview" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />} Conferir arquivo
        </Button>
      </div>

      {preview && (
        <div data-testid="restore-preview" className="rounded-xl border border-sky-800 bg-sky-950/40 p-5 space-y-3">
          <h2 className="font-display uppercase font-bold text-sky-200">2. Prévia</h2>
          <div className="text-sm text-sky-100">
            <div><b>Oficina do arquivo:</b> {preview.meta?.shop_name || "—"}</div>
            <div><b>Data do backup:</b> {fmtBR(preview.meta?.generated_at)}</div>
            <div><b>Origem:</b> {preview.meta?.source === "full_backup" ? "Backup completo do super admin" : "Backup do dono (.json)"}</div>
            <div><b>Totais:</b> {preview.totals.total_new} serão criados · {preview.totals.total_overwrite} existem hoje (serão sobrescritos no modo "voltar tudo")</div>
          </div>
          <table className="w-full text-xs text-zinc-200">
            <thead className="text-left text-zinc-400"><tr><th className="py-1">Coleção</th><th>Total</th><th>Novos</th><th>Já existem</th></tr></thead>
            <tbody>
              {preview.collections.map((c) => (
                <tr key={c.collection} data-testid={`restore-row-${c.collection}`} className="border-t border-zinc-800"><td className="py-1">{c.collection}</td><td>{c.total}</td><td className="text-emerald-300">{c.new}</td><td className="text-amber-200">{c.overwrite}</td></tr>
              ))}
            </tbody>
          </table>

          <div className="space-y-2 pt-3 border-t border-sky-900">
            <label className="flex items-start gap-2 text-sm"><input type="radio" name="mode" checked={mode === "insert_missing"} onChange={() => setMode("insert_missing")} data-testid="restore-mode-insert" /><span><b>Só repor o que sumiu</b> — insere apenas registros cujo ID não existe hoje. Nada atual é alterado.</span></label>
            <label className="flex items-start gap-2 text-sm"><input type="radio" name="mode" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="restore-mode-replace" /><span><b>Voltar tudo como estava</b> — substitui o conteúdo pelos dados do backup. O que foi criado depois do backup <b>fica como está</b>; o que foi alterado volta ao estado do backup.</span></label>
            <div className="pt-2">
              <label className="text-xs text-zinc-400 block mb-1">Para confirmar, digite o nome da oficina: <b>{shop?.name}</b></label>
              <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} data-testid="restore-confirm" className="w-full h-9 rounded-md bg-zinc-900 border border-zinc-700 px-3 text-sm text-zinc-100" placeholder={shop?.name || ""} />
            </div>
            <Button data-testid="restore-apply" disabled={busy || !confirmName} onClick={doRestore} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase mt-2">
              {busy === "restore" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />} Restaurar
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2"><History className="h-4 w-4 text-zinc-400" /><h2 className="font-display uppercase font-bold text-zinc-100">Histórico de restaurações</h2></div>
          <Button data-testid="restore-undo" disabled={!hasUndoable || busy} onClick={doUndo} variant="outline" className="bg-zinc-900 border-zinc-800 text-amber-200">
            {busy === "undo" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Undo2 className="h-4 w-4 mr-2" />} Desfazer última
          </Button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-zinc-500">Nenhuma restauração registrada ainda.</p>
        ) : (
          <ul data-testid="restore-history" className="divide-y divide-zinc-900 text-sm">
            {history.map((h) => (
              <li key={h._id} className="py-2">
                <div className="flex items-center gap-2 flex-wrap text-zinc-300">
                  <span className="text-zinc-400">{fmtBR(h.at)}</span>
                  <span>·</span>
                  <span className="font-semibold">{h.by_name}</span>
                  <span>·</span>
                  <span>{h.source?.kind === "undo" ? "desfez a última restauração" : h.mode === "replace" ? "voltou tudo como estava" : "só repôs o que sumiu"}</span>
                  {h.undone && h.source?.kind !== "undo" && <span className="text-xs rounded-full bg-amber-900/40 text-amber-200 px-2 py-0.5 ml-2">desfeito</span>}
                </div>
                <div className="text-xs text-zinc-500">{h.source?.filename || h.source?.kind} · +{h.counts?.inserted || 0} · ✎{h.counts?.updated || 0}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
